import { Select, Button, Collapse, NumberInput, TextInput } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import { ILocation } from '../../../libraries/location';
import { IEntity } from '../../../libraries/entity';
import { fetchApmPOSEquipmentList, IAPMPos } from '../../../libraries/apm/index';
import { IEquipmentOption, fetchEquipmentOptions } from '../../../libraries/equipment';
import { AppState } from '../../client-redux/reducers';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Language } from '../../languages/languageHandler';
import { ICurrency } from '../../../libraries/currencies';
import { formatTimeNSecLabel, getEndOfDay, getStartOfDay, toMoment } from '../../utils/date';
import { IUser } from '../../../libraries/user';
import React from 'react';
import { clearFilters, setFilters } from '../../client-redux/filterbar';
import classes from '../../components/util/date-time-picker.module.scss';
import { DateTimePicker } from '@mantine/dates';
import * as Icon from '@tabler/icons-react';
import './styles.scss';

export interface FilterBarFieldsData {
  entity?: number;
  uid?: number;
  eid?: number;
  lid?: number; //aka sid
  posid?: number;
  period?: number;
  transactionId?: string; //string to allow field to allow alphanumeric
  noteSN?: string; //string to allow field to allow alphanumeric
  currency?: number;
  beginDateTime?: Date | string;
  endDateTime?: Date | string;
  name?: string;
  financialOnly?: boolean;
  balance?: boolean;
  docCode?: string;
}

export interface FilterBarFieldsUsage {
  entity?: boolean;
  uid?: boolean;
  eid?: boolean;
  lid?: boolean; //aka sid
  posid?: boolean;
  period?: boolean;
  transactionId?: boolean;
  noteSN?: boolean;
  currency?: boolean;
  beginDateTime?: boolean;
  endDateTime?: boolean;
  name?: boolean;
  financialOnly?: boolean;
  balance?: boolean;
  docCode?: boolean;
}

interface FilterBarProps {
  /**
   * Triggered by the search button or enter key
   */
  onSearch: () => void;
  /**
   * Triggered by data change
   */
  onDataChange: (data: FilterBarFieldsData) => void;
  initialData?: FilterBarFieldsData;
  currenciesList?: ICurrency[];
  fieldsUsage?: FilterBarFieldsUsage;
  showSearchAndClear?: boolean;
  xlsReport?: () => void;
  pdfReport?: () => void;
  addButton?: () => void;
}

const FilterBar = ({
  onSearch,
  onDataChange,
  initialData,
  currenciesList,
  fieldsUsage,
  showSearchAndClear = true,
  xlsReport,
  pdfReport,
  addButton
}: FilterBarProps) => {
  const dispatch = useDispatch();

  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const [usage] = useState<FilterBarFieldsUsage>(fieldsUsage ?? {});
  const [data, setData] = useState<FilterBarFieldsData>(initialData ?? {});
  const user = useSelector<AppState, IUser>(state => state.login.user);
  const locations = useSelector<AppState, ILocation[]>(state => state.locations.locations);
  const entities = useSelector<AppState, IEntity[]>(state => state.entities.entities);
  const [posIdList, setPosIdList] = useState<IAPMPos[]>();
  const [equipmentList, setEquipmentList] = useState<IEquipmentOption[]>();
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState<boolean>(false);
  const [rawInputs, setRawInputs] = useState<Partial<FilterBarFieldsData>>({});
  const [currencyInitialized, setCurrencyInitialized] = useState(false);
  const [rawTransactionId, setRawTransactionId] = useState(data.transactionId ?? '');

  useEffect(() => {
    setDefaultData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = useCallback(
    (newData?: FilterBarFieldsData) => {
      if (!newData) {
        onSearch();
      } else {
        setData(prev => {
          const updated = { ...prev, ...newData };
          onDataChange(updated);
          dispatch(setFilters(updated));
          return updated;
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSearch, onDataChange]
  );

  useEffect(() => {
    setDefaultData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const controllerApmPOSEquipmentList = new AbortController();
  useEffect(() => {
    if (!usage?.posid) {
      return;
    }

    fetchApmPOSEquipmentList(controllerApmPOSEquipmentList, data?.entity || 0, data?.lid || 0)
      .then(response => {
        if (response?.statusOper?.code === 0 && response?.poss) {
          setPosIdList(response?.poss.length > 0 ? response.poss : undefined);
        } else {
          setPosIdList(undefined);
        }
      })
      .catch(error => {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          console.error('Error fetching pos equipment options:', error);
        }
      });
    return () => {
      controllerApmPOSEquipmentList.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.entity, data?.lid]);

  const controllerEquipmentOptions = new AbortController();

  useEffect(() => {
    if (!usage?.eid) {
      return;
    }
    fetchEquipmentOptions(data?.entity || 0, data?.lid || 0, controllerEquipmentOptions)
      .then(response => {
        if (response?.statusOper?.code === 0 && response?.list) {
          setEquipmentList(response.list.length > 0 ? response.list : undefined);
        } else {
          setEquipmentList(undefined);
        }
      })
      .catch(error => {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          console.error('Error fetching equipment options:', error);
        }
      });
    return () => {
      controllerEquipmentOptions.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.entity, data?.lid]);

  useEffect(() => {
    if (usage.currency && currenciesList && currenciesList.length > 0 && !currencyInitialized) {
      handleSearch({ ...data, currency: currenciesList[0].cid });
      setCurrencyInitialized(true);
    }
  }, [usage.currency, currenciesList, currencyInitialized, data, handleSearch]);

  const handleNumericInput = (field: keyof FilterBarFieldsData, value: string | number | null) => {
    const inputValue = value?.toString() ?? '';
    setRawInputs(prev => ({ ...prev, [field]: inputValue }));

    const numericValue = inputValue === '' || isNaN(Number(inputValue)) ? undefined : Number(inputValue);

    handleSearch({ ...data, [field]: numericValue });
  };

  const setDefaultData = () => {
    let newData: FilterBarFieldsData = {
      ...initialData
    };

    if (usage.financialOnly) {
      newData.financialOnly = true;
    }

    if (usage.entity && !newData.entity) {
      newData.entity = user?.entid;
    }

    if (usage.beginDateTime && !newData.beginDateTime) {
      newData.beginDateTime = getStartOfDay();
    }

    if (usage.endDateTime && !newData.endDateTime) {
      newData.endDateTime = getEndOfDay();
    }
    if (usage.balance && newData.balance === undefined) {
      newData.balance = undefined;
    }
    handleSearch(newData);
    setIsAdvancedSearchOpen(false);
  };

  /* Entity handle */
  const handleEntitySelect = (value: string | number | null) => {
    if (value !== null && data?.entity !== value) {
      const selectedEntid = Number(value);
      handleSearch({
        ...data,
        entity: selectedEntid,
        lid: 0, // Reset location selection when entity changes
        eid: 0, // Reset equipment selection when entity changes
        posid: 0 // Reset POS selection when entity changes
      });
    }
  };

  /* User ID handle */
  const handleUIDSelect = (value: string | number | null) => {
    handleNumericInput('uid', value);
  };

  /* Location handle */
  const handleNewTableBySid = (value: string | number | null) => {
    const numericValue = handleNumericInput('lid', value);
    if (numericValue !== undefined) {
      handleSearch({
        ...data,
        lid: numericValue,
        eid: 0,
        posid: 0
      });
    }
  };

  /* Equipment handle */
  const handleNewTableByEid = (value: string | number | null) => {
    const numericValue = handleNumericInput('eid', value);
    if (numericValue !== undefined) {
      handleSearch({ ...data, eid: numericValue });
    }
  };

  /* POS handle */
  const handleNewTableByPos = (value: string | number | null) => {
    const numericValue = handleNumericInput('posid', value);
    if (numericValue !== undefined) {
      handleSearch({ ...data, posid: numericValue });
    }
  };

  /* Period handle */
  const handleNewTableByPeriodId = (value: string | number | null) => {
    handleNumericInput('period', value);
  };

  /* Transaction ID handle */
  const handleTransactionIdChange = (value: string) => {
    setRawTransactionId(value);
  };

  /* Note SN handle */
  const handleNewTableByNotes = (value: string) => {
    handleSearch({ ...data, noteSN: value });
  };

  /* Currency handle */
  const handleNewTableByCurrency = (value: string | number | null) => {
    handleNumericInput('currency', value);
  };

  /* Name remains string */
  const handleNewTableByName = (value: string) => {
    handleSearch({ ...data, name: value });
  };

  /* Begin handle */
  const handleBeginDateChange = (date: string | null) => {
    let newDate: Date | undefined;
    if (date && date !== 'Invalid Date') {
      const picked = toMoment(date);
      if (picked.isValid()) {
        if (data?.endDateTime && picked.isAfter(toMoment(data?.endDateTime))) return;
        newDate = picked.toDate();
      }
    }
    //setData(prev => ({ ...prev, beginDateTime: newDate }));
    handleSearch({ ...data, beginDateTime: newDate });
  };

  /* End handle */
  const handleEndDateChange = (date: string | null) => {
    let newDate: Date | undefined;
    if (date && date !== 'Invalid Date') {
      const picked = toMoment(date);
      if (picked.isValid()) {
        if (data?.beginDateTime && picked.isBefore(toMoment(data?.beginDateTime))) return;
        newDate = picked.toDate();
      }
    }
    //setData(prev => ({ ...prev, endDateTime: newDate }));
    handleSearch({ ...data, endDateTime: newDate });
  };

  /* Financial handle */
  const handleFinancialOnlyChange = (value: string | number | null) => {
    //setData(prev => ({ ...prev, endDateTime: newDate }));
    handleSearch({ ...data, financialOnly: value === 1 ? true : false });
  };

  /* Balance  handle */
  const handleBalanceChange = (value: string | number | null) => {
    let balanceValue: boolean | undefined;

    if (value === '1' || value === 1) balanceValue = true;
    else if (value === '0' || value === 0) balanceValue = false;
    else balanceValue = undefined;

    handleSearch({ ...data, balance: balanceValue });
  };

  const toggleAdvancedSearch = useCallback(() => {
    setIsAdvancedSearchOpen(prev => !prev);
  }, []);

  /* DocCode remains string */
  const handleNewTableByDocCode = (value: string) => {
    handleSearch({ ...data, docCode: value });
  };

  const orderedFilterIds: string[] = [
    'entity',
    'location',
    'beginDate',
    'endDate',
    'currency',
    'uid',
    'equipment',
    'pos',
    'period',
    'transactionid',
    'noteSN',
    'name',
    'financialOnly',
    'balance',
    'docCode'
  ];

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLButtonElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Helper function to get a filter component's JSX
  const getFilterComponent = (filterId: string): JSX.Element | null => {
    switch (filterId) {
      case 'entity':
        return usage?.entity ? (
          <Select
            size="sm"
            withCheckIcon
            allowDeselect={false}
            label={lang.GLOBAL_ENTITY}
            data={
              entities?.map(entity => ({
                value: entity.entid.toString(),
                label: entity.name
              })) ?? []
            }
            value={data?.entity?.toString() ?? ''}
            onChange={value => handleEntitySelect(Number(value))}
          />
        ) : null;
      case 'uid':
        return usage?.uid ? (
          <NumberInput
            label={lang.USER_UID}
            value={data?.uid ?? undefined}
            onChange={handleUIDSelect}
            error={rawInputs.uid && isNaN(Number(rawInputs.uid)) && `${lang.GLOBAL_ONLY_NUMERIC_VALUES}`}
          />
        ) : null;
      case 'location':
        return usage?.lid ? (
          <Select
            withCheckIcon
            allowDeselect={false}
            label={lang.EQUIPMENT_LOCAL}
            data={[
              { value: '0', label: lang.GLOBAL_ALL },
              ...(locations
                ?.filter(elem => elem.entid === data?.entity)
                .map(location => ({
                  value: location?.sid.toString(),
                  label: location?.name || ''
                })) ?? [])
            ]}
            value={data?.lid?.toString() ?? '0'}
            onChange={value => handleNewTableBySid(Number(value))}
            disabled={data?.entity === 0}
          />
        ) : null;
      case 'beginDate':
        return usage?.beginDateTime ? (
          <DateTimePicker
            classNames={{
              day: classes.day
            }}
            clearable
            label={lang.TRANSACTION_INITIAL_DATE}
            value={data?.beginDateTime}
            defaultValue={data?.beginDateTime ? formatTimeNSecLabel(data?.beginDateTime) : undefined}
            onChange={handleBeginDateChange}
            maxDate={data?.endDateTime ?? undefined}
            placeholder={lang.GLOBAL_PICK_A_DATE}
            valueFormat="DD/MM/YYYY HH:mm"
            timePickerProps={{
              withDropdown: true,
              popoverProps: { withinPortal: false },
              format: '24h',
              classNames: {
                input: classes.timeInput,
                field: classes.timeField,
                control: classes.timeControl
              }
            }}
          />
        ) : null;

      case 'endDate':
        return usage?.endDateTime ? (
          <DateTimePicker
            classNames={{
              day: classes.day
            }}
            clearable
            label={lang.TRANSACTION_END_DATE}
            value={data?.endDateTime}
            defaultValue={data?.endDateTime ? formatTimeNSecLabel(data?.endDateTime) : undefined}
            onChange={handleEndDateChange}
            minDate={data?.beginDateTime ?? undefined}
            maxDate={getEndOfDay()}
            placeholder={lang.GLOBAL_PICK_A_DATE}
            valueFormat="DD/MM/YYYY HH:mm"
            timePickerProps={{
              withDropdown: true,
              popoverProps: { withinPortal: false },
              format: '24h',
              classNames: {
                input: classes.timeInput,
                field: classes.timeField,
                control: classes.timeControl
              }
            }}
          />
        ) : null;

      case 'currency':
        return usage?.currency ? (
          <Select
            withCheckIcon
            allowDeselect={false}
            label={lang.GLOBAL_CURRENCY}
            data={
              currenciesList?.map(currency => ({
                value: currency.cid.toString(),
                label: currency.description
              })) ?? []
            }
            value={data?.currency?.toString() ?? ''}
            onChange={value => handleNewTableByCurrency(Number(value))}
          />
        ) : null;
      case 'equipment':
        return usage?.eid ? (
          <Select
            withCheckIcon
            allowDeselect={false}
            label={lang.GLOBAL_EQUIPMENT}
            data={
              equipmentList
                ? [
                    { value: '0', label: lang.GLOBAL_ALL },
                    ...equipmentList?.map(item => ({
                      value: item.eid.toString(),
                      label: `${item.eid} ${item.label.replace(`${item.eid}`, '').trim()}`
                    }))
                  ]
                : [{ value: '0', label: lang.GLOBAL_ALL }]
            }
            value={data?.eid?.toString() ?? '0'}
            onChange={value => handleNewTableByEid(Number(value))}
          />
        ) : null;
      case 'pos':
        return usage?.posid ? (
          <Select
            withCheckIcon
            allowDeselect={false}
            label="POS"
            data={[
              { value: '0', label: lang.GLOBAL_ALL },
              ...(posIdList?.map(pos => ({
                value: pos.posid.toString(),
                label: pos.description ? `${pos.posid} - ${pos.description}` : `${pos.posid}`
              })) ?? [])
            ]}
            value={data?.posid?.toString() ?? '0'}
            onChange={value => handleNewTableByPos(Number(value))}
          />
        ) : null;
      case 'period':
        return usage?.period ? (
          <NumberInput
            label={lang.PROCESSED_GLASS_PERIOD}
            min={1}
            value={data?.period || ''}
            onChange={handleNewTableByPeriodId}
            disabled={!data?.eid}
            onKeyDown={handleKeyDown}
            error={rawInputs.period && isNaN(Number(rawInputs.period)) && `${lang.GLOBAL_ONLY_NUMERIC_VALUES}`}
          />
        ) : null;
      case 'transactionid':
        return usage?.transactionId ? (
          <TextInput
            label={lang.TRANSACTION_ID}
            value={rawTransactionId}
            onChange={e => handleTransactionIdChange(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleSearch({ ...data, transactionId: rawTransactionId });
              }
            }}
          />
        ) : null;

      case 'noteSN':
        return usage?.noteSN ? (
          <TextInput
            label={lang.TRANSACTION_NOTE_SN}
            value={data?.noteSN ?? ''}
            onChange={e => handleNewTableByNotes(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        ) : null;
      case 'name':
        return usage?.name ? (
          <TextInput
            label={lang.GLOBAL_NAME}
            value={data?.name || ''}
            onChange={e => handleNewTableByName(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        ) : null;

      case 'financialOnly':
        return usage?.financialOnly ? (
          <Select
            withCheckIcon
            allowDeselect={false}
            label={lang.TRANSACTION_TYPE}
            value={data.financialOnly === false ? '0' : '1'}
            data={[
              { value: '1', label: lang.TRANSACTION_FINANCIAL_ONLY },
              { value: '0', label: lang.GLOBAL_ALL }
            ]}
            onChange={value => handleFinancialOnlyChange(Number(value))}
          />
        ) : null;

      case 'balance':
        return usage?.balance ? (
          <Select
            withCheckIcon
            allowDeselect={false}
            label={lang.BRISA_USERS_SUMMARIES_WITH_BALANCE}
            value={data.balance === true ? '1' : data.balance === false ? '0' : ''}
            data={[
              { value: '', label: lang.GLOBAL_ALL },
              { value: '1', label: lang.YES },
              { value: '0', label: lang.NO }
            ]}
            onChange={value => handleBalanceChange(value)}
          />
        ) : null;

      case 'docCode':
        return usage?.docCode ? (
          <TextInput
            label={lang.TRANSACTION_DOC}
            value={data?.docCode || ''}
            onChange={e => handleNewTableByDocCode(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        ) : null;
      default:
        return null;
    }
  };

  const mainFiltersToRender: JSX.Element[] = [];
  const advancedFiltersToRender: JSX.Element[] = [];
  let renderedFilterCount = 0;

  orderedFilterIds.forEach(id => {
    const filterComponent = getFilterComponent(id);
    if (filterComponent) {
      if (renderedFilterCount < 4) {
        mainFiltersToRender.push(<React.Fragment key={id}>{filterComponent}</React.Fragment>);
        renderedFilterCount++;
      } else {
        advancedFiltersToRender.push(<React.Fragment key={id}>{filterComponent}</React.Fragment>);
      }
    }
  });

  // Determine if the advanced section should be visible/toggleable
  const hasAdvancedFilters = advancedFiltersToRender.length > 0;
  const showAdvancedSearchButton = hasAdvancedFilters;

  return (
    <div className="filterBar-container">
      {/* Main row: inputs + buttons */}
      <div className="filterbar-row">
        <div className="filters main-row">{mainFiltersToRender}</div>

        <div className="buttons">
          {/* Open advanced filters */}
          {showAdvancedSearchButton && (
            <Button radius="md" onClick={toggleAdvancedSearch}>
              {isAdvancedSearchOpen ? (
                <Icon.IconChevronsUp stroke={2} size={28} />
              ) : (
                <Icon.IconChevronsDown stroke={2} size={28} />
              )}
            </Button>
          )}

          {/* Clear filters */}
          {showSearchAndClear && fieldsUsage && (
            <Button
              radius="md"
              onClick={() => {
                dispatch(clearFilters());
                setDefaultData();
              }}
            >
              <Icon.IconFilterX stroke={2} size={28} />
            </Button>
          )}

          {/* Search */}
          {showSearchAndClear && (
            <Button
              radius="md"
              className="search-button"
              onClick={() => handleSearch({ ...data, transactionId: rawTransactionId })}
            >
              <Icon.IconRefresh stroke={2} size={28} />
            </Button>
          )}

          {/* Excel */}
          {xlsReport && (
            <Button radius="md" onClick={xlsReport}>
              <Icon.IconFileTypeXls stroke={2} />
            </Button>
          )}

          {/* PDF */}
          {pdfReport && (
            <Button radius="md" onClick={pdfReport}>
              <Icon.IconFileTypePdf stroke={2} />
            </Button>
          )}

          {/* Add Button */}
          {addButton && (
            <Button radius="md" onClick={addButton}>
              <Icon.IconPlus stroke={2} />
            </Button>
          )}
        </div>
      </div>

      {/* Advanced filters */}
      {hasAdvancedFilters && (
        <Collapse in={isAdvancedSearchOpen}>
          <div className="filters advanced-row">{advancedFiltersToRender}</div>
        </Collapse>
      )}
    </div>
  );
};

export default FilterBar;
